// Calendar — Google Calendar-style layout with sidebar + grid views
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { Empty, Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";

// ─── Types ────────────────────────────────────────────────────────────────────
type CalEvent = {
  id: number; title: string; start_at: string; end_at: string;
  location: string | null; visibility: string; description?: string | null;
  color?: string | null; is_all_day?: boolean;
};
type Resource = { id: number; name: string; type: string; capacity: number | null; location: string | null; is_active: boolean };
type Booking  = { id: number; resource_id: number; start_at: string; end_at: string; status: string; user_id: number };
type GoogleStatus = { connected: boolean; google_email?: string; last_synced_at?: string | null };
type ViewMode = "day" | "week" | "month" | "list";
interface GoogleCal { id: string; summary: string; color: string; primary: boolean; selected: boolean }

// ─── Helpers ──────────────────────────────────────────────────────────────────
const VIS_COLOR: Record<string, string> = {
  PRIVATE: "#6366f1", DEPARTMENT: "#f59e0b", COMPANY: "#10b981",
};
const getColor = (e: CalEvent) => e.color || VIS_COLOR[e.visibility] || "#6366f1";

function fmt(d: Date, opts: Intl.DateTimeFormatOptions) { return d.toLocaleString("ko-KR", opts); }
function pad2(n: number) { return String(n).padStart(2, "0"); }
function toLocalDT(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const GoogleIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// ─── Router ───────────────────────────────────────────────────────────────────
export default function CalendarPage() {
  const [params] = useSearchParams();
  const filter = params.get("filter") || "";
  if (filter === "rooms")  return <ResourcesView />;
  if (filter === "google") return <GoogleSyncView />;
  return <CalendarApp />;
}

// ─── CalendarApp ─────────────────────────────────────────────────────────────
function CalendarApp() {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode: ViewMode = (searchParams.get("view") as ViewMode) || "month";
  const [cursor, setCursor] = useState(() => new Date());
  const [events, setEvents] = useState<CalEvent[] | null>(null);
  const [selected, setSelected] = useState<CalEvent | null>(null);
  const [createDate, setCreateDate] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [visFilter, setVisFilter] = useState<Set<string>>(() => new Set(["PRIVATE", "DEPARTMENT", "COMPANY"]));

  const setView = (v: ViewMode) =>
    setSearchParams((p) => { const n = new URLSearchParams(p); n.set("view", v); return n; });

  const range = useMemo(() => {
    if (viewMode === "day") {
      const s = new Date(cursor); s.setHours(0,0,0,0);
      const e = new Date(cursor); e.setHours(23,59,59,999);
      return { start: s.toISOString(), end: e.toISOString() };
    }
    if (viewMode === "week") {
      const d = new Date(cursor);
      const s = new Date(d); s.setDate(d.getDate()-d.getDay()); s.setHours(0,0,0,0);
      const e = new Date(s); e.setDate(s.getDate()+6); e.setHours(23,59,59,999);
      return { start: s.toISOString(), end: e.toISOString() };
    }
    const s = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const e = new Date(cursor.getFullYear(), cursor.getMonth()+1, 0, 23, 59, 59);
    return { start: s.toISOString(), end: e.toISOString() };
  }, [viewMode, cursor]);

  const reload = useCallback(() => {
    setEvents(null);
    api.get<CalEvent[]>(`/calendar/events?start=${range.start}&end=${range.end}&scope=company`)
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]));
  }, [range.start, range.end]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = (dateStr = "") => { setCreateDate(dateStr); setCreateOpen(true); };
  const deleteEvent = async (id: number) => {
    try { await api.delete(`/calendar/events/${id}`); toast.success("일정 삭제됨"); setSelected(null); reload(); }
    catch { toast.error("삭제 실패"); }
  };
  const navigate = (dir: -1 | 1) => setCursor((prev) => {
    const d = new Date(prev);
    if (viewMode === "day")  d.setDate(d.getDate() + dir);
    else if (viewMode === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    return d;
  });

  const filtered = useMemo(() =>
    (events ?? []).filter((e) => visFilter.has(e.visibility)),
    [events, visFilter]
  );

  const navLabel = useMemo(() => {
    if (viewMode === "day") return fmt(cursor, { year: "numeric", month: "long", day: "numeric", weekday: "long" });
    if (viewMode === "week") {
      const d = new Date(cursor);
      const s = new Date(d); s.setDate(d.getDate()-d.getDay());
      const e = new Date(s); e.setDate(s.getDate()+6);
      return `${fmt(s,{month:"short",day:"numeric"})} – ${fmt(e,{month:"short",day:"numeric"})}`;
    }
    return `${cursor.getFullYear()}년 ${cursor.getMonth()+1}월`;
  }, [viewMode, cursor]);

  const toggleVis = (key: string) => setVisFilter((prev) => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden", background:"var(--bg-0,#fff)" }}>
      <CalSidebar
        cursor={cursor}
        onDateClick={setCursor}
        visFilter={visFilter}
        onToggleVis={toggleVis}
        onCreateClick={() => openCreate()}
      />
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        {/* Header */}
        <div style={{
          padding:"10px 16px", borderBottom:"1px solid var(--border)",
          display:"flex", alignItems:"center", gap:8, flexShrink:0,
          background:"var(--bg-0)", boxShadow:"0 1px 0 var(--border)",
        }}>
          <button className="t-btn" onClick={() => navigate(-1)} style={{padding:"4px 6px"}}><I.ChevL size={14}/></button>
          <button className="t-btn" onClick={() => navigate(1)}  style={{padding:"4px 6px"}}><I.ChevR size={14}/></button>
          <button className="t-btn" onClick={() => setCursor(new Date())}
            style={{fontSize:12,padding:"5px 12px",border:"1px solid var(--border)",borderRadius:6}}>
            오늘
          </button>
          <h2 style={{margin:0,fontSize:18,fontWeight:600,flex:1,paddingLeft:4}}>{navLabel}</h2>

          {/* View toggle */}
          <div style={{display:"flex",borderRadius:8,overflow:"hidden",border:"1px solid var(--border)"}}>
            {(["day","week","month","list"] as ViewMode[]).map((v) => (
              <button key={v} onClick={() => setView(v)} style={{
                padding:"5px 13px", fontSize:12, border:"none", cursor:"pointer",
                background: viewMode===v ? "var(--p-500,#6366f1)" : "var(--panel)",
                color: viewMode===v ? "#fff" : "var(--text-2)",
                fontWeight: viewMode===v ? 600 : 400,
                transition:"all 0.15s",
              }}>
                {v==="day"?"일간":v==="week"?"주간":v==="month"?"월간":"목록"}
              </button>
            ))}
          </div>
          <GoogleSyncButton onSynced={reload} />
        </div>

        {/* Calendar view area */}
        <div style={{flex:1,overflow:"auto",padding:viewMode==="list"?"12px 16px":0}}>
          {events===null ? <Loading /> : (
            <>
              {viewMode==="month" && <MonthGrid cursor={cursor} events={filtered} onEventClick={setSelected} onDateClick={(d)=>openCreate(d)}/>}
              {viewMode==="week"  && <WeekGrid cursor={cursor} events={filtered} onEventClick={setSelected} onSlotClick={(d)=>openCreate(d)}/>}
              {viewMode==="day"   && <DayView  cursor={cursor} events={filtered} onEventClick={setSelected} onSlotClick={(d)=>openCreate(d)}/>}
              {viewMode==="list"  && <ListView events={filtered} onEventClick={setSelected} onAdd={()=>openCreate()}/>}
            </>
          )}
        </div>
      </div>

      {selected && <EventDetailModal event={selected} onClose={()=>setSelected(null)} onDelete={()=>deleteEvent(selected.id)}/>}
      {createOpen && <CreateModal initialDate={createDate} onClose={()=>{setCreateOpen(false);reload();}}/>}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const CAL_ITEMS = [
  { key:"PRIVATE",    label:"내 일정 (기본)", color:"#6366f1" },
  { key:"DEPARTMENT", label:"부서 일정",       color:"#f59e0b" },
  { key:"COMPANY",    label:"전사 일정",       color:"#10b981" },
];

function CalSidebar({ cursor, onDateClick, visFilter, onToggleVis, onCreateClick }: {
  cursor: Date; onDateClick:(d:Date)=>void;
  visFilter:Set<string>; onToggleVis:(k:string)=>void; onCreateClick:()=>void;
}) {
  const nav = useNavigate();
  const [miniCursor, setMiniCursor] = useState(() => new Date(cursor));
  const [gStatus, setGStatus] = useState<{connected:boolean}|null>(null);

  useEffect(() => {
    api.get<{connected:boolean}>("/calendar/google/status")
      .then((r)=>setGStatus(r.data)).catch(()=>setGStatus({connected:false}));
  }, []);

  // Keep mini-cal in sync when cursor changes externally
  useEffect(() => { setMiniCursor(new Date(cursor)); }, [cursor.getFullYear(), cursor.getMonth()]);

  return (
    <div style={{
      width:210, flexShrink:0, borderRight:"1px solid var(--border)",
      display:"flex", flexDirection:"column", background:"var(--bg-0)", overflow:"auto",
    }}>
      <div style={{padding:"14px 12px 8px"}}>
        <button className="t-btn" onClick={onCreateClick} style={{
          width:"100%", padding:"10px 0", fontSize:14, fontWeight:600, borderRadius:24,
          background:"var(--p-500,#6366f1)", color:"#fff", border:"none", cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"center", gap:6,
          boxShadow:"0 2px 8px rgba(99,102,241,0.4)",
        }}>
          <span style={{fontSize:18,lineHeight:1}}>+</span> 일정 등록
        </button>
      </div>

      <MiniCal cursor={miniCursor} selectedDate={cursor}
        onDateClick={(d)=>{ setMiniCursor(d); onDateClick(d); }}
        onMonthChange={setMiniCursor} />

      <div style={{height:1,background:"var(--border)",margin:"6px 0"}}/>

      {/* 내 캘린더 */}
      <div style={{padding:"4px 14px 8px"}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",marginBottom:6,letterSpacing:"0.05em",textTransform:"uppercase"}}>
          내 캘린더
        </div>
        {CAL_ITEMS.map((item)=>(
          <label key={item.key} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 4px",cursor:"pointer",borderRadius:6}}>
            <span style={{
              width:16, height:16, borderRadius:4, flexShrink:0,
              background: visFilter.has(item.key) ? item.color : "transparent",
              border:`2px solid ${item.color}`,
              display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s",
            }}>
              {visFilter.has(item.key) && (
                <svg width="9" height="7" viewBox="0 0 9 7">
                  <path d="M1 3.5l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </span>
            <input type="checkbox" hidden checked={visFilter.has(item.key)} onChange={()=>onToggleVis(item.key)}/>
            <span style={{fontSize:12,color:"var(--text-1)"}}>{item.label}</span>
          </label>
        ))}
      </div>

      <div style={{height:1,background:"var(--border)",margin:"4px 0"}}/>

      {/* 외부 연동 */}
      <div style={{padding:"4px 14px 8px"}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",marginBottom:6,letterSpacing:"0.05em",textTransform:"uppercase"}}>
          외부 연동
        </div>
        <button className="t-btn" onClick={()=>nav("/calendar?filter=google")} style={{
          width:"100%", justifyContent:"flex-start", gap:8, fontSize:12, padding:"5px 4px",
        }}>
          <GoogleIcon size={13}/>
          <span style={{flex:1,textAlign:"left"}}>Google 캘린더</span>
          {gStatus && <span style={{
            width:7,height:7,borderRadius:"50%",flexShrink:0,
            background: gStatus.connected ? "#22c55e" : "var(--text-3)",
          }}/>}
        </button>
      </div>

      <div style={{height:1,background:"var(--border)",margin:"4px 0"}}/>

      {/* 자원 예약 */}
      <div style={{padding:"4px 14px 8px"}}>
        <button className="t-btn" onClick={()=>nav("/calendar?filter=rooms")} style={{
          width:"100%", justifyContent:"flex-start", gap:8, fontSize:12, padding:"5px 4px",
        }}>
          <span>🏢</span> 자원 예약
        </button>
      </div>

      <div style={{flex:1}}/>

      <div style={{padding:"8px 14px",borderTop:"1px solid var(--border)"}}>
        <button className="t-btn" style={{fontSize:11,color:"var(--text-3)",padding:"3px 0",gap:5}}>
          <span>⚙</span> 캘린더 환경설정
        </button>
      </div>
    </div>
  );
}

// ─── Mini Calendar ─────────────────────────────────────────────────────────────
function MiniCal({ cursor, selectedDate, onDateClick, onMonthChange }: {
  cursor:Date; selectedDate:Date;
  onDateClick:(d:Date)=>void; onMonthChange:(d:Date)=>void;
}) {
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const today = new Date();
  const cells: (number|null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({length:daysInMonth},(_,i)=>i+1),
  ];
  while (cells.length%7!==0) cells.push(null);

  const prev = () => { const d=new Date(cursor); d.setMonth(d.getMonth()-1); onMonthChange(d); };
  const next = () => { const d=new Date(cursor); d.setMonth(d.getMonth()+1); onMonthChange(d); };

  return (
    <div style={{padding:"4px 10px 4px"}}>
      <div style={{display:"flex",alignItems:"center",marginBottom:4}}>
        <button className="t-btn" onClick={prev} style={{padding:"2px 4px"}}><I.ChevL size={11}/></button>
        <span style={{flex:1,textAlign:"center",fontSize:12,fontWeight:600}}>
          {year}년 {month+1}월
        </span>
        <button className="t-btn" onClick={next} style={{padding:"2px 4px"}}><I.ChevR size={11}/></button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)"}}>
        {["일","월","화","수","목","금","토"].map((d,i)=>(
          <div key={d} style={{textAlign:"center",fontSize:10,fontWeight:600,padding:"1px 0",
            color:i===0?"#ef4444":i===6?"#3b82f6":"var(--text-3)"}}>{d}</div>
        ))}
        {cells.map((day,idx)=>{
          const col=idx%7;
          const isToday = day!=null && day===today.getDate() && month===today.getMonth() && year===today.getFullYear();
          const isSelected = day!=null && day===selectedDate.getDate() && month===selectedDate.getMonth() && year===selectedDate.getFullYear();
          return (
            <div key={idx} onClick={()=>{if(day){onDateClick(new Date(year,month,day));}}} style={{
              textAlign:"center", fontSize:11, padding:"2px 1px", borderRadius:"50%",
              cursor:day?"pointer":undefined, lineHeight:"20px", width:20, margin:"1px auto",
              background: isToday?"var(--p-500,#6366f1)": isSelected?"var(--p-100,#e0e7ff)":undefined,
              color: isToday?"#fff": col===0?"#ef4444": col===6?"#3b82f6": day?"var(--text-1)":undefined,
              fontWeight: isToday||isSelected?700:400,
              opacity:day?1:0,
            }}>{day}</div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Month Grid ───────────────────────────────────────────────────────────────
function MonthGrid({ cursor, events, onEventClick, onDateClick }: {
  cursor:Date; events:CalEvent[];
  onEventClick:(e:CalEvent)=>void; onDateClick:(s:string)=>void;
}) {
  const year=cursor.getFullYear(), month=cursor.getMonth();
  const firstDay=new Date(year,month,1).getDay();
  const daysInMonth=new Date(year,month+1,0).getDate();
  const today=new Date();
  const cells:(number|null)[]=[...Array(firstDay).fill(null),...Array.from({length:daysInMonth},(_,i)=>i+1)];
  while(cells.length%7!==0) cells.push(null);

  const eventsOnDay=(day:number)=>events.filter((e)=>{
    const s=new Date(e.start_at);
    return s.getFullYear()===year && s.getMonth()===month && s.getDate()===day;
  });

  const isToday=(day:number)=>day===today.getDate()&&month===today.getMonth()&&year===today.getFullYear();
  const DAYS=["일","월","화","수","목","금","토"];

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",minHeight:0}}>
      {/* Day headers */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",borderBottom:"1px solid var(--border)",background:"var(--panel)"}}>
        {DAYS.map((d,i)=>(
          <div key={d} style={{padding:"8px 0",textAlign:"center",fontSize:12,fontWeight:600,
            color:i===0?"#ef4444":i===6?"#3b82f6":"var(--text-2)"}}>{d}</div>
        ))}
      </div>
      {/* Grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",flex:1,gridAutoRows:"1fr"}}>
        {cells.map((day,idx)=>{
          const col=idx%7;
          const dayEvs=day?eventsOnDay(day):[];
          const dateStr=day?`${year}-${pad2(month+1)}-${pad2(day)}`:"";
          const baseBg=!day?"var(--bg-1,var(--panel))":((col===0||col===6)?"var(--bg-1,#fafbfc)":"transparent");
          return (
            <div key={idx} style={{
              borderTop:"1px solid var(--border)",
              borderLeft:col>0?"1px solid var(--border)":undefined,
              padding:"6px 5px 4px",
              background:baseBg,
              cursor:day?"pointer":undefined, minHeight:100, transition:"background 0.1s",
            }}
              onMouseEnter={day?(e)=>{e.currentTarget.style.background="var(--hover)";}:undefined}
              onMouseLeave={day?(e)=>{e.currentTarget.style.background=baseBg;}:undefined}
              onClick={()=>day&&onDateClick(`${dateStr}T09:00`)}>
              {day&&(
                <>
                  <div style={{
                    fontSize:12,fontWeight:600,marginBottom:4,
                    width:24,height:24,borderRadius:"50%",
                    background:isToday(day)?"var(--p-500,#6366f1)":undefined,
                    color:isToday(day)?"#fff":col===0?"#ef4444":col===6?"#3b82f6":"var(--text-1)",
                    display:"flex",alignItems:"center",justifyContent:"center",
                  }}>{day}</div>
                  <div style={{display:"flex",flexDirection:"column",gap:2}}>
                    {dayEvs.slice(0,3).map((e)=>(
                      <div key={e.id} onClick={(ev)=>{ev.stopPropagation();onEventClick(e);}} style={{
                        fontSize:11,padding:"2px 6px",borderRadius:4,cursor:"pointer",
                        background:`${getColor(e)}18`,
                        borderLeft:`3px solid ${getColor(e)}`,
                        color:getColor(e),
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                        fontWeight:500, lineHeight:"16px",
                      }}>
                        {!e.is_all_day&&(
                          <span style={{opacity:0.75,marginRight:3,fontSize:10}}>
                            {fmt(new Date(e.start_at),{hour:"2-digit",minute:"2-digit"})}
                          </span>
                        )}
                        {e.title}
                      </div>
                    ))}
                    {dayEvs.length>3&&(
                      <div style={{fontSize:10,color:"var(--text-3)",paddingLeft:6}}>+{dayEvs.length-3}개 더</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week Grid ────────────────────────────────────────────────────────────────
const HOURS=Array.from({length:24},(_,i)=>i);
const CELL_H=52;

function WeekGrid({ cursor, events, onEventClick, onSlotClick }: {
  cursor:Date; events:CalEvent[];
  onEventClick:(e:CalEvent)=>void; onSlotClick:(s:string)=>void;
}) {
  const DAYS=["일","월","화","수","목","금","토"];
  const today=new Date();
  const weekStart=new Date(cursor);
  weekStart.setDate(cursor.getDate()-cursor.getDay()); weekStart.setHours(0,0,0,0);
  const weekDays=Array.from({length:7},(_,i)=>{ const d=new Date(weekStart); d.setDate(weekStart.getDate()+i); return d; });

  const eventsOnDay=(d:Date)=>events.filter((e)=>isSameDay(new Date(e.start_at),d));

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Day headers */}
      <div style={{display:"grid",gridTemplateColumns:"52px repeat(7,1fr)",background:"var(--panel)",borderBottom:"1px solid var(--border)",flexShrink:0}}>
        <div/>
        {weekDays.map((d,i)=>(
          <div key={i} style={{padding:"8px 4px",textAlign:"center"}}>
            <div style={{fontSize:11,color:i===0?"#ef4444":i===6?"#3b82f6":"var(--text-3)",fontWeight:500}}>{DAYS[i]}</div>
            <div style={{
              fontSize:20,fontWeight:700,width:34,height:34,borderRadius:"50%",margin:"2px auto 0",
              background:isSameDay(d,today)?"var(--p-500,#6366f1)":undefined,
              color:isSameDay(d,today)?"#fff":i===0?"#ef4444":i===6?"#3b82f6":"var(--text-1)",
              display:"flex",alignItems:"center",justifyContent:"center",
            }}>{d.getDate()}</div>
          </div>
        ))}
      </div>
      {/* Time grid */}
      <div style={{overflowY:"auto",flex:1}}>
        <div style={{display:"grid",gridTemplateColumns:"52px repeat(7,1fr)"}}>
          {HOURS.map((h)=>(
            <Fragment key={h}>
              <div style={{
                borderTop:"1px solid var(--border)",height:CELL_H,
                padding:"3px 6px 0",fontSize:10,color:"var(--text-3)",textAlign:"right",
              }}>{h>0?`${pad2(h)}:00`:""}</div>
              {weekDays.map((d,di)=>{
                const dayEvs=eventsOnDay(d).filter((e)=>new Date(e.start_at).getHours()===h);
                const slot=`${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}T${pad2(h)}:00`;
                return (
                  <div key={`${h}-${di}`} style={{
                    borderTop:"1px solid var(--border)",borderLeft:"1px solid var(--border)",
                    height:CELL_H,padding:"2px 3px",cursor:"pointer",
                    background:isSameDay(d,today)?"rgba(99,102,241,0.03)":undefined,
                  }} onClick={()=>onSlotClick(slot)}>
                    {dayEvs.map((e)=>(
                      <div key={e.id} onClick={(ev)=>{ev.stopPropagation();onEventClick(e);}} style={{
                        fontSize:11,padding:"2px 5px",borderRadius:4,marginBottom:1,
                        background:`${getColor(e)}20`,borderLeft:`3px solid ${getColor(e)}`,
                        color:getColor(e),overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                        cursor:"pointer",fontWeight:500,
                      }}>{e.title}</div>
                    ))}
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Day View ─────────────────────────────────────────────────────────────────
function DayView({ cursor, events, onEventClick, onSlotClick }: {
  cursor:Date; events:CalEvent[];
  onEventClick:(e:CalEvent)=>void; onSlotClick:(s:string)=>void;
}) {
  const today=new Date();
  const isToday=isSameDay(cursor,today);
  const dayEvs=events.filter((e)=>isSameDay(new Date(e.start_at),cursor));

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Day header */}
      <div style={{padding:"12px 20px",borderBottom:"1px solid var(--border)",background:"var(--panel)",flexShrink:0,display:"flex",alignItems:"center",gap:12}}>
        <div style={{
          fontSize:28,fontWeight:700,width:44,height:44,borderRadius:"50%",
          background:isToday?"var(--p-500,#6366f1)":undefined,
          color:isToday?"#fff":"var(--text-1)",
          display:"flex",alignItems:"center",justifyContent:"center",
        }}>{cursor.getDate()}</div>
        <div>
          <div style={{fontSize:14,fontWeight:600,color:"var(--text-1)"}}>
            {fmt(cursor,{weekday:"long"})}
          </div>
          <div style={{fontSize:12,color:"var(--text-3)"}}>
            {dayEvs.length}개 일정
          </div>
        </div>
      </div>
      {/* Time column */}
      <div style={{overflowY:"auto",flex:1}}>
        <div style={{display:"grid",gridTemplateColumns:"52px 1fr"}}>
          {HOURS.map((h)=>{
            const hEvs=dayEvs.filter((e)=>new Date(e.start_at).getHours()===h);
            const slot=`${cursor.getFullYear()}-${pad2(cursor.getMonth()+1)}-${pad2(cursor.getDate())}T${pad2(h)}:00`;
            return (
              <Fragment key={h}>
                <div style={{borderTop:"1px solid var(--border)",height:CELL_H,
                  padding:"3px 8px 0",fontSize:11,color:"var(--text-3)",textAlign:"right",
                }}>{h>0?`${pad2(h)}:00`:""}</div>
                <div style={{
                  borderTop:"1px solid var(--border)",borderLeft:"1px solid var(--border)",
                  height:CELL_H,padding:"3px 8px",cursor:"pointer",
                }} onClick={()=>onSlotClick(slot)}>
                  {hEvs.map((e)=>(
                    <div key={e.id} onClick={(ev)=>{ev.stopPropagation();onEventClick(e);}} style={{
                      fontSize:12,padding:"4px 10px",borderRadius:6,marginBottom:2,
                      background:`${getColor(e)}18`,borderLeft:`4px solid ${getColor(e)}`,
                      color:"var(--text-1)",cursor:"pointer",
                    }}>
                      <span style={{fontWeight:600}}>{e.title}</span>
                      {e.location&&<span style={{marginLeft:8,fontSize:11,color:"var(--text-3)"}}>📍{e.location}</span>}
                    </div>
                  ))}
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────
function ListView({ events, onEventClick }: {
  events:CalEvent[]; onEventClick:(e:CalEvent)=>void; onAdd?:()=>void;
}) {
  if (!events.length) return <Empty msg="이번 달 일정이 없습니다"/>;
  const sorted=[...events].sort((a,b)=>a.start_at.localeCompare(b.start_at));

  return (
    <div className="t-card" style={{padding:0}}>
      <div className="t-list" style={{padding:"8px 0"}}>
        {sorted.map((e)=>(
          <div key={e.id} className="t-row" style={{cursor:"pointer",padding:"10px 16px",gap:12}} onClick={()=>onEventClick(e)}>
            <span style={{
              width:12,height:12,borderRadius:"50%",background:getColor(e),flexShrink:0,marginTop:2,
            }}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,fontSize:13,marginBottom:2}}>{e.title}</div>
              <div style={{fontSize:12,color:"var(--text-3)"}}>
                {fmt(new Date(e.start_at),{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                {e.location&&<span style={{marginLeft:8}}>📍{e.location}</span>}
              </div>
            </div>
            <Pill kind={e.visibility==="PRIVATE"?"muted":e.visibility==="COMPANY"?"ok":"info"}>
              {e.visibility==="PRIVATE"?"비공개":e.visibility==="DEPARTMENT"?"부서":"전사"}
            </Pill>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Event Detail Modal ───────────────────────────────────────────────────────
function EventDetailModal({ event, onClose, onDelete }: {
  event:CalEvent; onClose:()=>void; onDelete:()=>void;
}) {
  const color=getColor(event);
  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e)=>e.stopPropagation()} style={{width:420,borderTop:`4px solid ${color}`}}>
        <div style={{padding:"16px 18px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:color,flexShrink:0}}/>
          <h3 style={{margin:0,flex:1,fontSize:15}}>{event.title}</h3>
          <button className="t-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{padding:18,display:"grid",gap:10}}>
          <ModalRow icon="🕐" label="시작">{fmt(new Date(event.start_at),{year:"numeric",month:"long",day:"numeric",weekday:"short",hour:"2-digit",minute:"2-digit"})}</ModalRow>
          <ModalRow icon="🕐" label="종료">{fmt(new Date(event.end_at),{year:"numeric",month:"long",day:"numeric",hour:"2-digit",minute:"2-digit"})}</ModalRow>
          {event.location&&<ModalRow icon="📍" label="장소">{event.location}</ModalRow>}
          <ModalRow icon="👁" label="공개">
            <Pill kind={event.visibility==="PRIVATE"?"muted":event.visibility==="COMPANY"?"ok":"info"}>
              {event.visibility==="PRIVATE"?"비공개":event.visibility==="DEPARTMENT"?"부서":"전사"}
            </Pill>
          </ModalRow>
          {event.description&&<ModalRow icon="📝" label="설명">{event.description}</ModalRow>}
        </div>
        <div style={{padding:"12px 18px",borderTop:"1px solid var(--border)",display:"flex",justifyContent:"flex-end",gap:8}}>
          <button className="t-btn" style={{color:"#ef4444"}} onClick={onDelete}>삭제</button>
          <button className="t-btn" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
function ModalRow({ icon, label, children }: { icon:string; label:string; children:React.ReactNode }) {
  return (
    <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
      <span style={{width:20,textAlign:"center",flexShrink:0}}>{icon}</span>
      <span style={{width:54,fontSize:12,color:"var(--text-3)",flexShrink:0}}>{label}</span>
      <span style={{fontSize:13,flex:1}}>{children}</span>
    </div>
  );
}

// ─── Create Modal ─────────────────────────────────────────────────────────────
function CreateModal({ onClose, initialDate }: { onClose:()=>void; initialDate?:string }) {
  const [form, setForm] = useState({
    title:"", start_at:toLocalDT(initialDate||""), end_at:"",
    location:"", visibility:"PRIVATE", description:"", google_calendar_id:"",
  });
  const [googleCals, setGoogleCals] = useState<GoogleCal[]>([]);

  useEffect(()=>{
    api.get<{connected:boolean}>("/calendar/google/status").then((r)=>{
      if(r.data.connected) api.get<{calendars:GoogleCal[]}>("/calendar/google/calendars").then((cr)=>{
        const cals=cr.data.calendars??[];
        setGoogleCals(cals);
        const primary=cals.find((c)=>c.primary);
        if(primary) setForm((f)=>({...f,google_calendar_id:primary.id}));
      }).catch(()=>{});
    }).catch(()=>{});
  },[]);

  const submit=async()=>{
    try {
      const payload:Record<string,unknown>={...form};
      if(!form.end_at) delete payload.end_at;
      if(!form.google_calendar_id) delete payload.google_calendar_id;
      await api.post("/calendar/events",payload);
      toast.success("일정 추가됨"); onClose();
    } catch(e:any){ toast.error("실패",e?.response?.data?.error?.message); }
  };

  const selectedCal=googleCals.find((c)=>c.id===form.google_calendar_id);

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e)=>e.stopPropagation()} style={{width:500}}>
        <div style={{padding:"16px 18px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:10}}>
          <h3 style={{margin:0,flex:1}}>일정 추가</h3>
          <button className="t-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{padding:18,display:"grid",gap:12,maxHeight:"70vh",overflowY:"auto"}}>
          {/* Google 캘린더 선택 */}
          {googleCals.length>0&&(
            <div>
              <label className="t-label">Google 캘린더</label>
              <div style={{position:"relative"}}>
                {selectedCal&&<span style={{
                  position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",
                  width:10,height:10,borderRadius:3,background:selectedCal.color,
                  border:"1px solid rgba(0,0,0,0.15)",pointerEvents:"none",zIndex:1,
                }}/>}
                <select className="t-input" value={form.google_calendar_id}
                  onChange={(e)=>setForm({...form,google_calendar_id:e.target.value})}
                  style={{paddingLeft:selectedCal?28:undefined}}>
                  <option value="">저장 안 함 (Tim-box만)</option>
                  {googleCals.map((c)=>(
                    <option key={c.id} value={c.id}>{c.summary}{c.primary?" (기본)":""}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="t-label">제목 *</label>
            <input className="t-input" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} placeholder="일정 제목" autoFocus/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <label className="t-label">시작 *</label>
              <input type="datetime-local" className="t-input" value={form.start_at} onChange={(e)=>setForm({...form,start_at:e.target.value})}/>
            </div>
            <div>
              <label className="t-label">종료</label>
              <input type="datetime-local" className="t-input" value={form.end_at} onChange={(e)=>setForm({...form,end_at:e.target.value})}/>
            </div>
          </div>
          <div>
            <label className="t-label">장소</label>
            <input className="t-input" value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} placeholder="선택 사항"/>
          </div>
          <div>
            <label className="t-label">공개 범위</label>
            <select className="t-input" value={form.visibility} onChange={(e)=>setForm({...form,visibility:e.target.value})}>
              <option value="PRIVATE">비공개 (내 일정)</option>
              <option value="DEPARTMENT">부서 공개</option>
              <option value="COMPANY">전사 공개</option>
            </select>
          </div>
          <div>
            <label className="t-label">설명</label>
            <textarea className="t-input" rows={3} value={form.description}
              onChange={(e)=>setForm({...form,description:e.target.value})} placeholder="선택 사항" style={{resize:"vertical"}}/>
          </div>
        </div>
        <div style={{padding:"12px 18px",borderTop:"1px solid var(--border)",display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={!form.title||!form.start_at}>저장</button>
        </div>
      </div>
    </div>
  );
}

// ─── Google Sync Button ───────────────────────────────────────────────────────
function GoogleSyncButton({ onSynced }: { onSynced:()=>void }) {
  const [status, setStatus] = useState<GoogleStatus|null>(null);
  const [syncing, setSyncing] = useState(false);
  const nav = useNavigate();

  useEffect(()=>{
    api.get<GoogleStatus>("/calendar/google/status").then((r)=>setStatus(r.data)).catch(()=>setStatus({connected:false}));
  },[]);

  const handleClick=async()=>{
    if(!status) return;
    if(!status.connected){ nav("/calendar?filter=google"); return; }
    setSyncing(true);
    try {
      const r=await api.post<{synced_at:string;imported:number;exported:number}>("/calendar/google/sync");
      toast.success("Google 동기화 완료",`${r.data.imported}개 가져옴`);
      setStatus((s)=>s?{...s,last_synced_at:r.data.synced_at}:s);
      onSynced();
    } catch(e:any){ toast.error("동기화 실패",e?.response?.data?.error?.message??""); }
    finally{ setSyncing(false); }
  };

  const connected=status?.connected??false;
  const lastSynced=status?.last_synced_at;
  return (
    <button className="t-btn" onClick={handleClick} disabled={syncing}
      title={connected?`Google 동기화${lastSynced?` · 마지막: ${new Date(lastSynced).toLocaleString("ko-KR",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}`:""}`:
        "Google 캘린더 연결"}
      style={{display:"flex",alignItems:"center",gap:5,fontSize:12,padding:"5px 10px",border:"1px solid var(--border)",borderRadius:6}}>
      <GoogleIcon size={13}/>
      {syncing?<span style={{color:"var(--text-3)"}}>동기화 중…</span>:<>
        <span>{connected?"동기화":"연결"}</span>
        <span style={{width:6,height:6,borderRadius:"50%",background:connected?"#22c55e":"var(--text-3)",flexShrink:0}}/>
      </>}
    </button>
  );
}

// ─── Resources View ───────────────────────────────────────────────────────────
function ResourcesView() {
  const [resources, setResources] = useState<Resource[]|null>(null);
  const [bookings,  setBookings]  = useState<Booking[]>([]);
  const [form, setForm] = useState({resource_id:0,start_at:"",end_at:""});
  const nav = useNavigate();

  const load=async()=>{
    const [rr,br]=await Promise.all([
      api.get<Resource[]>("/calendar/resources"),
      api.get<Booking[]>("/calendar/bookings?scope=mine").catch(()=>({data:[] as Booking[]})),
    ]);
    setResources(rr.data); setBookings(br.data);
  };
  useEffect(()=>{load();},[]);

  const book=async()=>{
    try{ await api.post("/calendar/bookings",form); toast.success("예약됨"); setForm({resource_id:0,start_at:"",end_at:""}); load(); }
    catch(e:any){ toast.error("예약 실패",e?.response?.data?.error?.message??""); }
  };
  const cancel=async(id:number)=>{
    try{ await api.delete(`/calendar/bookings/${id}`); toast.success("취소됨"); load(); }catch{}
  };

  if(resources===null) return <Loading/>;

  return (
    <div style={{padding:16}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
        <button className="t-btn" onClick={()=>nav("/calendar")}>← 캘린더로</button>
        <h2 style={{margin:0,fontSize:18}}>자원 예약</h2>
      </div>
      <div className="t-grid cols-1">
        <div className="t-card">
          <h3 style={{margin:"0 0 12px"}}>자원 목록</h3>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
            {resources.filter((r)=>r.is_active).map((r)=>(
              <div key={r.id} style={{border:"1px solid var(--border)",borderRadius:8,padding:12,
                background:form.resource_id===r.id?"var(--p-100,rgba(99,102,241,0.1))":undefined}}>
                <div style={{fontWeight:600,fontSize:13}}>{r.name}</div>
                <div style={{fontSize:11,color:"var(--text-3)"}}>{r.type}{r.capacity?` · ${r.capacity}인`:""}</div>
                {r.location&&<div style={{fontSize:11,color:"var(--text-3)"}}>{r.location}</div>}
                <button className="t-btn" style={{fontSize:11,marginTop:8,padding:"3px 10px",
                  background:form.resource_id===r.id?"var(--p-500,#6366f1)":undefined,
                  color:form.resource_id===r.id?"#fff":undefined}}
                  onClick={()=>setForm({...form,resource_id:r.id})}>
                  {form.resource_id===r.id?"✓ 선택됨":"선택"}
                </button>
              </div>
            ))}
          </div>
          {!resources.filter(r=>r.is_active).length&&<Empty msg="등록된 자원이 없습니다"/>}
        </div>
        {form.resource_id>0&&(
          <div className="t-card" style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10,alignItems:"flex-end",maxWidth:600}}>
            <div><label className="t-label">시작</label><input type="datetime-local" className="t-input" value={form.start_at} onChange={(e)=>setForm({...form,start_at:e.target.value})}/></div>
            <div><label className="t-label">종료</label><input type="datetime-local" className="t-input" value={form.end_at} onChange={(e)=>setForm({...form,end_at:e.target.value})}/></div>
            <button className="t-btn t-btn-primary" onClick={book} disabled={!form.start_at||!form.end_at}>예약</button>
          </div>
        )}
        {bookings.length>0&&(
          <div className="t-card">
            <div style={{fontWeight:600,marginBottom:10}}>내 예약</div>
            <div className="t-list">
              {bookings.map((b)=>(
                <div key={b.id} className="t-row">
                  <span style={{flex:1,fontSize:13}}>
                    자원 #{b.resource_id} · {fmt(new Date(b.start_at),{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                  </span>
                  <Pill kind={b.status==="BOOKED"?"ok":"muted"}>{b.status}</Pill>
                  <button className="t-btn" style={{fontSize:12}} onClick={()=>cancel(b.id)}>취소</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Google Sync View ─────────────────────────────────────────────────────────
function GoogleSyncView() {
  const [status,   setStatus]    = useState<GoogleStatus|null>(null);
  const [syncing,  setSyncing]   = useState(false);
  const [calendars,setCalendars] = useState<GoogleCal[]>([]);
  const [searchParams,setSearchParams] = useSearchParams();
  const nav = useNavigate();

  const load=()=>
    api.get<GoogleStatus>("/calendar/google/status").then((r)=>{
      setStatus(r.data);
      if(r.data.connected) api.get<{calendars:GoogleCal[]}>("/calendar/google/calendars")
        .then((cr)=>setCalendars(cr.data.calendars??[])).catch(()=>{});
    }).catch(()=>setStatus({connected:false}));

  useEffect(()=>{
    const connected=searchParams.get("connected");
    const oauthError=searchParams.get("error");
    if(connected==="1"){
      setTimeout(()=>toast.success("Google 캘린더 연결 완료","동기화 버튼을 눌러 일정을 가져오세요."),300);
      setSearchParams((p)=>{const n=new URLSearchParams(p);n.delete("connected");return n;});
    } else if(oauthError){
      const msg:Record<string,string>={
        token_exchange_failed:"Google 인증 코드 교환에 실패했습니다.",
        missing_secret:"GOOGLE_OAUTH_CLIENT_SECRET이 설정되지 않았습니다.",
        access_denied:"Google 계정 연결을 취소했습니다.",
      };
      setTimeout(()=>toast.error("Google 연결 실패",msg[oauthError]??oauthError),300);
      setSearchParams((p)=>{const n=new URLSearchParams(p);n.delete("error");return n;});
    }
    load();
  },[]);

  const connect=async()=>{
    try {
      const r=await api.get<{auth_url:string|null}>("/calendar/google/auth/begin");
      if(r.data.auth_url) window.location.href=r.data.auth_url;
      else toast.error("Google OAuth 미설정","관리자에게 GOOGLE_OAUTH_CLIENT_ID 설정을 요청하세요.");
    }catch{}
  };
  const disconnect=async()=>{
    try{ await api.delete("/calendar/google/disconnect"); toast.success("연결 해제됨"); load(); }catch{}
  };
  const sync=async()=>{
    setSyncing(true);
    try{
      const r=await api.post<{synced_at:string;imported:number;exported:number}>("/calendar/google/sync");
      toast.success("동기화 완료",`가져옴 ${r.data.imported}개 · 내보냄 ${r.data.exported}개`);
      load();
    }catch(e:any){ toast.error("동기화 실패",e?.response?.data?.error?.message??""); }
    finally{ setSyncing(false); }
  };

  if(!status) return <Loading/>;

  return (
    <div style={{padding:20,maxWidth:560}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
        <button className="t-btn" onClick={()=>nav("/calendar")}>← 캘린더로</button>
        <h2 style={{margin:0,fontSize:18}}>Google 캘린더 연동</h2>
      </div>
      <div className="t-card" style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <GoogleIcon size={30}/>
          <div>
            <div style={{fontWeight:700,fontSize:14}}>Google 캘린더</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>Tim-box ↔ Google 양방향 동기화</div>
          </div>
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:status.connected?"#22c55e":"var(--text-3)",display:"inline-block"}}/>
            <span style={{fontSize:12,color:status.connected?"#22c55e":"var(--text-3)",fontWeight:600}}>
              {status.connected?"연결됨":"미연결"}
            </span>
          </div>
        </div>

        {status.connected?(
          <>
            {status.google_email&&(
              <div style={{fontSize:13,color:"var(--text-2)",background:"var(--panel)",padding:"8px 12px",borderRadius:8}}>
                📧 {status.google_email}
              </div>
            )}
            {status.last_synced_at&&(
              <div style={{fontSize:12,color:"var(--text-3)"}}>
                마지막 동기화: {fmt(new Date(status.last_synced_at),{year:"numeric",month:"long",day:"numeric",hour:"2-digit",minute:"2-digit"})}
              </div>
            )}
            <div style={{display:"flex",gap:8}}>
              <button className="t-btn t-btn-primary" onClick={sync} disabled={syncing} style={{fontSize:13}}>
                {syncing?"동기화 중…":"🔄 지금 동기화"}
              </button>
              <button className="t-btn" onClick={disconnect} style={{fontSize:13}}>연결 해제</button>
            </div>
            {calendars.length>0&&(
              <div>
                <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                  내 캘린더 ({calendars.length}개)
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {calendars.map((c)=>(
                    <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 8px",borderRadius:6,background:"var(--panel)"}}>
                      <span style={{width:12,height:12,borderRadius:3,background:c.color,flexShrink:0,border:"1px solid rgba(0,0,0,0.1)"}}/>
                      <span style={{fontSize:12,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.summary}</span>
                      {c.primary&&<span style={{fontSize:10,color:"var(--text-3)",background:"var(--bg-1)",padding:"1px 5px",borderRadius:4}}>기본</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{fontSize:11,color:"var(--text-3)",lineHeight:1.6}}>
              동기화 시 모든 캘린더의 일정을 가져옵니다 (과거 30일 ~ 미래 90일).
            </div>
          </>
        ):(
          <>
            <p style={{margin:0,fontSize:13,color:"var(--text-2)",lineHeight:1.7}}>
              Google 계정을 연결하면 Tim-box 캘린더와 Google 캘린더가 양방향 동기화됩니다.
            </p>
            <button className="t-btn t-btn-primary" style={{maxWidth:200,fontSize:13}} onClick={connect}>
              Google 계정 연결
            </button>
          </>
        )}
      </div>
    </div>
  );
}
